import dayjs from 'dayjs';
import {
  ISearchQuery,
  IInboxNotes,
  IInboxLeadReduced,
} from '@llws/platform-shared-types/types/api/lead-warehouse';
import {
  IApiResponse,
  IExportFilterPayload,
  ILeadStoreState,
  IMetaResponse,
  TInboxLead,
  TInboxLeadNote,
  TInboxLeadSingle,
  TLead,
  TUpdateLead,
} from '@/shared';
import leadsIngest from '@/shared/services/leads-ingest';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { downloadCSV } from '@/shared/helpers/download-csv';

export default {
  namespaced: true, // 'lead-warehouse/'

  state: {
    list: undefined,
    single: undefined,
    notes: undefined,
  } as ILeadStoreState,

  getters: {

    list(state: ILeadStoreState): IInboxLeadReduced[] {
      if (state.list?.data) {
        return state.list.data.map((l: TInboxLead) => ({
          ...l._source as IInboxLeadReduced,
        }));
      }
      return [];
    },

    listMeta(state: ILeadStoreState): IMetaResponse | undefined {
      return state.list && { ...state.list.meta as IMetaResponse };
    },

    single(state:ILeadStoreState): TLead | undefined {
      if (state.single?.data) {
        return { ...state.single.data._source } as TLead;
      }
      return undefined;
    },

    notes(state: ILeadStoreState): IInboxNotes[] | undefined {
      if (state.notes?.data) {
        return state.notes.data.map((note: TInboxLeadNote) => ({
          ...note._source as IInboxNotes,
        }));
      }
      return [];
    },

  },

  mutations: {
    set(
      state: ILeadStoreState,
      payload: TInboxLeadSingle | TInboxLead[] | IApiResponse<TInboxLead[] | TInboxLeadSingle, IMetaResponse>,
    ) {
      if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
        if (Array.isArray((payload as IApiResponse<TInboxLead[], IMetaResponse>).data)) {
          state.list = payload as IApiResponse<TInboxLead[], IMetaResponse>;
        } else {
          state.single = payload as IApiResponse<TInboxLeadSingle, IMetaResponse>;
        }
      } else if (Array.isArray(payload as TInboxLead[])) {
        state.list = {
          ...state.list && state.list,
          ...{ data: payload as TInboxLead[] },
        };
      } else {
        state.single = {
          ...state.single && state.single,
          ...{ data: payload as TInboxLeadSingle },
        };
      }
    },

    setNotes: (state: ILeadStoreState, payload: IApiResponse<TInboxLeadNote[], IMetaResponse>) => {
      state.notes = payload;
    },

    reset(state: ILeadStoreState) {
      state.list = undefined;
      state.single = undefined;
    },

    resetNotes: (state: ILeadStoreState) => {
      state.notes = undefined;
    },

  },

  actions: {
    async get(ctx: IAction<ILeadStoreState>, payload: ISearchQuery & {id?: string}) {
      const result: IApiResponse<TInboxLead | TInboxLead[], IMetaResponse | null> = payload?.id
        ? await leadsIngest.getById(payload.id)
        : await leadsIngest.get(payload);
      ctx.commit('set', result);
    },

    async getCSV(ctx: IAction<IStateStrict<IInboxLeadReduced>>, payload: IExportFilterPayload & {searchQueryParams: ISearchQuery}) {
      const result = await leadsIngest.getExport({ ...payload.searchQueryParams, fields: payload.fields }, '?csvExport=SAME_METHOD');

      if (payload.doDownload) {
        downloadCSV(typeof result === 'string' ? result : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
      }
      return result;
    },

    async getNotes(ctx: IAction<ILeadStoreState>, payload: Record<string, string>) {
      const result: IApiResponse<TInboxLeadNote[], IMetaResponse | null > = await leadsIngest.getLeadNotes(payload);
      ctx.commit('setNotes', result);
    },

    async postNote(ctx: IAction<ILeadStoreState>, payload: IInboxNotes) {
      const currentState = ctx.state.notes;
      const result = (await leadsIngest.postNotes(payload)).data as IInboxNotes;
      const newData = {
        _index: '',
        _type: '_doc',
        _id: result.id,
        _score: 0,
        _source: result,
      } as TInboxLeadNote;
      currentState!.data!.push(newData);
      ctx.commit('setNotes', currentState);
    },

    async updateLead(ctx: IAction<ILeadStoreState>, payload: TUpdateLead) {
      const singleState = ctx.state.single?.data as TInboxLeadSingle;
      const listState = ctx.state.list?.data?.find((v: TInboxLead) => v._id === payload.leadId);

      const listObject = ctx.state.list?.data as TInboxLead[];

      if (ctx.state.list && listState?._source) {
        if (payload.method in listState._source) {
          listState._source = Object.assign(listState._source, { [payload.method]: payload.status });
        }

        listObject.map((obj) => listState._id === obj._id || obj);
        ctx.commit('set', listObject);
        await leadsIngest.updateLeads(payload);
      }

      if (ctx.state.single && singleState?._source) {
        if (payload.method in singleState._source) {
          singleState._source = Object.assign(singleState._source, { [payload.method]: payload.status });
        }

        ctx.commit('set', singleState);
        await leadsIngest.updateLeads(payload);
      }
      const result = await leadsIngest.updateLeads(payload);
    },

    async deleteNote(ctx: IAction<ILeadStoreState>, id: string) {
      const result = await leadsIngest.deleteNotes(id);
      const currentState = ctx.state.notes;
      if (currentState?.data) {
        const index = currentState.data.findIndex((cState) => cState._id === id);
        currentState.data.splice(index, 1);
      }

      ctx.commit('setNotes', currentState);
    },

  },
};
